import React, { useState, useEffect } from "react";

import { t } from "i18next";
import { isEmpty } from "lodash";
import { Trans } from "react-i18next";
import Select from "react-select";

import BaseModal from "./BaseModal";
import EditableImage from "../Images/EditableImages";
import { getReactSelectStyling } from "../../utils/ReactSelectStyles";
import ValidationError from "../Shared/ValidationError";
import { ErrorAlert } from "../../utils/Alerts";
import { validate } from "../../utils/ValidationHelpers";
import materialSuppliersSchema from "../../schemas/materialSuppliers.schema";
import { reformatCountry } from "../../utils/CountryHelper";

const MAX_MATERIAL_LIMIT = 20;

const SuppliersModal = ({
    isVisible,
    setIsVisible,
    isModalSubmitting,
    data,
    setData,
    editAction,
    addAction,
    countriesLookup,
    materialsLookup,
    error,
    setError,
}) => {
    const [formData, setFormData] = useState({});

    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    useEffect(() => {
        validate(
            getCleanFormData(),
            materialSuppliersSchema.UpdateSuppliersSchema,
            setIsFormValid,
            setError
        );
    }, [formData]);

    useEffect(() => {
        setFormData(JSON.parse(JSON.stringify(data)));
    }, [data]);

    useEffect(() => {
        if (!isVisible) resetForm();
    }, [isVisible]);

    const getCleanFormData = () => {
        const getCleanArray = (arr) => {
            return arr?.filter((item) => !item.isDeleted);
        };

        return {
            ...formData,
            materials: getCleanArray(formData.materials),
        };
    };

    const getTitle = () => {
        return isEmpty(data) ? t("addSupplier") : t("editSupplier");
    };

    const resetForm = () => {
        setIsFormSubmitted(false);
        setIsFormValid(false);
        setError({});
        setData({});
    };

    const getUpdatePayload = () => {
        const getDataValues = (arr) => {
            return arr?.map((record) => ({
                id: record.value,
                isDeleted: record.isDeleted,
            }));
        };

        return {
            id: formData?.id,
            name: formData?.name,
            codeName: formData?.codeName,
            image: formData?.image,
            description: formData?.description || null,
            city: formData?.city || null,
            state: formData?.state || null,
            supportedCountryId: formData?.supportedCountry?.value,
            materials: getDataValues(formData?.materials),
        };
    };

    const getCreatePayload = () => {
        const getIdValues = (data) => {
            return (
                data &&
                data.map((record) => ({
                    id: record.value,
                }))
            );
        };

        return {
            name: formData?.name,
            codeName: formData?.codeName,
            image: formData?.image,
            description: formData?.description || null,
            city: formData?.city || null,
            state: formData?.state || null,
            materialIds: getIdValues(formData?.materials),
            supportedCountryId: formData?.supportedCountry?.value,
        };
    };

    const handleActionClicked = () => {
        setIsFormSubmitted(true);
        if (!isFormValid) return;
        isEmpty(data)
            ? addAction(getCreatePayload())
            : editAction(getUpdatePayload());
    };

    const returnErrorMessages = (propName) => {
        return isFormSubmitted && error && error[propName];
    };

    const handleInputChange = (e) => {
        setFormData((prevData) => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));
    };

    const doesValueExistInData = (propName, finderPropName, finderVal) => {
        return (
            data[propName] &&
            data[propName].find(
                (record) => record[finderPropName] === finderVal
            )
        );
    };

    const isValueMarkedDeleted = (propName, finderPropName, finderVal) => {
        return (
            formData[propName] &&
            formData[propName].find(
                (record) =>
                    record[finderPropName] === finderVal && record.isDeleted
            )
        );
    };

    const showLimitReachedError = (value, maxLimit, entity) => {
        if (maxLimit && maxLimit < value.length) {
            ErrorAlert(t("maxReach", { number: maxLimit.toString(), entity }));
            return true;
        }
    };

    const clearMultiSelect = (propName) => {
        const newRecords =
            data[propName] &&
            data[propName].map((record) => ({
                ...record,
                isDeleted: true,
            }));

        setFormData((prevData) => ({
            ...prevData,
            [propName]: newRecords,
        }));
    };

    const selectActionMultiSelect = (
        propName,
        finderPropName,
        finderVal,
        option
    ) => {
        const isMarkedDeleted = isValueMarkedDeleted(
            propName,
            finderPropName,
            finderVal
        );

        let newEntities = (formData && formData[propName]) || [];

        if (isMarkedDeleted) {
            newEntities = newEntities.map((record) =>
                record[finderPropName] === finderVal
                    ? { ...record, isDeleted: false }
                    : record
            );
        } else {
            newEntities.push(option);
        }

        setFormData((prevData) => ({
            ...prevData,
            [propName]: newEntities,
        }));
    };

    const removeActionMultiSelect = (propName, finderPropName, finderVal) => {
        const recordExists = doesValueExistInData(
            propName,
            finderPropName,
            finderVal
        );

        let newEntities = (formData && formData[propName]) || [];

        if (recordExists) {
            newEntities = newEntities.map((record) =>
                record[finderPropName] === finderVal
                    ? { ...record, isDeleted: true }
                    : record
            );
        } else {
            newEntities = newEntities.filter(
                (record) => record.value !== finderVal
            );
        }
        setFormData((prevData) => ({
            ...prevData,
            [propName]: newEntities,
        }));
    };

    const onSelectChange = (
        value,
        action,
        maxLimit,
        entityLocalized,
        condition,
        includeCountriesFormat = false
    ) => {
        if (showLimitReachedError(value, maxLimit, entityLocalized)) return;
        if (condition) return;

        switch (action.action) {
            case "remove-value": {
                const selecedId = action.removedValue.value;

                removeActionMultiSelect(action.name, "value", selecedId);
                break;
            }

            case "select-option": {
                const selectedValue = action.option;
                const selecedId = selectedValue.value;
                const countryOption = {
                    label: selectedValue.searchable,
                    value: selectedValue.value,
                    alpha2Code: selectedValue.code,
                };

                selectActionMultiSelect(
                    action.name,
                    "value",
                    selecedId,
                    includeCountriesFormat ? countryOption : selectedValue
                );
                break;
            }

            case "clear": {
                clearMultiSelect(action.name);
                break;
            }

            default:
                return;
        }
    };

    const getValidValues = (records) => {
        return records && records.filter((country) => !country.isDeleted);
    };

    const fields = [
        {
            name: "name",
            isRequired: true,
            label: <Trans i18nKey="name">Name</Trans>,
            input: (
                <input
                    type="text"
                    className="input-element"
                    placeholder={t("firstNamePlaceholder")}
                    onChange={handleInputChange}
                    name={"name"}
                    value={formData?.name || ""}
                />
            ),
        },
        {
            name: "codeName",
            label: <Trans i18nKey="codeName">codeName</Trans>,
            input: (
                <input
                    type="text"
                    className="input-element"
                    placeholder={t("codeNamePlaceholder")}
                    onChange={handleInputChange}
                    name={"codeName"}
                    value={formData?.codeName || ""}
                />
            ),
        },
        {
            name: "materials",
            isRequired: true,
            label: <Trans i18nKey="materials">Materials</Trans>,
            input: (
                <Select
                    isMulti
                    placeholder={t("selectPlaceholder", {
                        entity: t("materials"),
                    })}
                    name="materials"
                    value={getValidValues(formData?.materials) || ""}
                    onChange={(value, action) =>
                        onSelectChange(
                            value,
                            action,
                            MAX_MATERIAL_LIMIT,
                            t("materials")
                        )
                    }
                    options={materialsLookup}
                    styles={getReactSelectStyling({
                        showError: returnErrorMessages("materials"),
                        isMultiSelect: true,
                    })}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                />
            ),
        },
        {
            name: "supportedCountry",
            isRequired: true,
            label: <Trans i18nKey="country">country</Trans>,
            input: (
                <Select
                    placeholder={t("selectPlaceholder", {
                        entity: t("country"),
                    })}
                    value={
                        formData?.supportedCountry
                            ? reformatCountry(formData?.supportedCountry)
                            : ""
                    }
                    name="supportedCountries"
                    onChange={(value) =>
                        setFormData((prevData) => ({
                            ...prevData,
                            supportedCountry: value,
                        }))
                    }
                    options={countriesLookup}
                    styles={getReactSelectStyling({
                        showError: returnErrorMessages("supportedCountry"),
                        isMultiSelect: true,
                    })}
                    getOptionValue={(option) => option.searchable}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                />
            ),
        },
        {
            name: "city",
            label: <Trans i18nKey="city">city</Trans>,
            input: (
                <input
                    type="text"
                    className="input-element"
                    placeholder={t("cityPlaceholder")}
                    onChange={handleInputChange}
                    name={"city"}
                    value={formData?.city || ""}
                />
            ),
        },
        {
            name: "state",
            label: <Trans i18nKey="state">state</Trans>,
            input: (
                <input
                    type="text"
                    className="input-element"
                    placeholder={t("statePlaceholder")}
                    onChange={handleInputChange}
                    name={"state"}
                    value={formData?.state || ""}
                />
            ),
        },
        {
            name: "description",
            label: <Trans i18nKey="description">Description</Trans>,
            input: (
                <textarea
                    type="text"
                    className="input-element textarea-element"
                    placeholder={t("description")}
                    onChange={handleInputChange}
                    name={"description"}
                    value={formData?.description || ""}
                ></textarea>
            ),
        },
    ];

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                const errorMessage = returnErrorMessages(field.name);

                return (
                    <div
                        key={index}
                        className={`input-group ${
                            errorMessage ? "invalid-warning-input" : ""
                        }`}
                    >
                        <label
                            className={`uppercase ${
                                field.isRequired ? "required" : ""
                            }`}
                        >
                            {field.label}
                        </label>
                        {field.input}
                        <ValidationError errorMessage={errorMessage} />
                    </div>
                );
            })
        );
    };

    const renderUploadImage = () => {
        return (
            <EditableImage
                state={formData}
                stateSetter={setFormData}
                defaultImage={
                    require("../../assets/images/org-avatar.png").default
                }
            />
        );
    };

    return (
        <BaseModal
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            isSubmitting={isModalSubmitting}
            title={getTitle()}
            action={{
                submitAction: handleActionClicked,
            }}
        >
            <div className="main-customer-form-inputs">
                <div className="upload-img-box flex">
                    <div className="relative">{renderUploadImage()}</div>
                    <div className="w-full">{renderFields()}</div>
                </div>
            </div>
        </BaseModal>
    );
};

export default SuppliersModal;
